// src/api/features/promos/mutations.ts

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createPromo,
  updatePromo,
  deletePromo,
  generatePromoCodes,
  simulatePromo,
} from "./api";
import { PromosKeys } from "./queries";
import type {
  Promo,
  CreatePromoPayload,
  UpdatePromoPayload,
  GenerateCodeBatchPayload,
  GenerateCodeBatchResult,
  SimulatePayload,
  SimulateResult,
} from "./types";

export function useCreatePromoMutation() {
  const qc = useQueryClient();
  return useMutation<Promo, Error, CreatePromoPayload>({
    mutationFn: (payload) => createPromo(payload),
    onSuccess() {
      qc.invalidateQueries({ queryKey: PromosKeys.all });
    },
  });
}

export function useUpdatePromoMutation(id: number | string) {
  const qc = useQueryClient();
  return useMutation<Promo, Error, UpdatePromoPayload>({
    mutationFn: (payload) => updatePromo(id, payload),
    onSuccess() {
      qc.invalidateQueries({ queryKey: PromosKeys.all });
      qc.invalidateQueries({ queryKey: PromosKeys.detail(id) });
    },
  });
}

export function useSetPromoStatusMutation() {
  const qc = useQueryClient();
  return useMutation<Promo, Error, { id: number | string; status: Promo["status"] }>({
    mutationFn: ({ id, status }) => updatePromo(id, { status }),
    onSuccess() {
      qc.invalidateQueries({ queryKey: PromosKeys.all });
    },
  });
}

export function useDeletePromoMutation() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, number | string>({
    mutationFn: (id) => deletePromo(id),
    onSuccess(_res, id) {
      qc.removeQueries({ queryKey: PromosKeys.detail(id) });
      qc.invalidateQueries({ queryKey: PromosKeys.all });
    },
  });
}

export function useGeneratePromoCodesMutation(id: number | string) {
  const qc = useQueryClient();
  return useMutation<GenerateCodeBatchResult, Error, GenerateCodeBatchPayload>({
    mutationFn: (payload) => generatePromoCodes(id, payload),
    onSuccess() {
      qc.invalidateQueries({ queryKey: PromosKeys.detail(id) });
    },
  });
}

export function useSimulatePromoMutation(id: number | string) {
  return useMutation<SimulateResult, Error, SimulatePayload>({
    mutationFn: (payload) => simulatePromo(id, payload),
  });
}
